/**
 * Time Grid Drag Hook
 *
 * Handles pointer events for drag-to-move and drag-to-resize
 * on the time grid calendar. Supports:
 * - Moving an event/task to a different time slot
 * - Resizing an event/task from top (start time) or bottom (end time)
 * - Creating a new event by clicking on an empty slot
 */

import { useState, useCallback, useRef } from 'react';
import { startOfDay } from 'date-fns';
import {
  PIXELS_PER_MS,
  DEFAULT_START_HOUR,
  snapToSlot,
} from '@/utils/scheduleLayout';

export type DragMode = 'move' | 'resize-top' | 'resize-bottom' | null;

interface DragState {
  itemId: string;
  mode: DragMode;
  originalStartTime: number;
  originalEndTime: number;
  currentStartTime: number;
  currentEndTime: number;
  dayDate: Date;
  /** Y offset from pointer to item top, for smooth dragging */
  pointerOffsetY: number;
}

interface UseTimeGridDragOptions {
  startHour?: number;
  snapIntervalMs?: number;
  minDurationMs?: number;
  onUpdateSchedule: (
    itemId: string,
    scheduledStart: number,
    scheduledEnd: number
  ) => void;
  onSlotClick?: (date: Date, timeMs: number) => void;
}

interface UseTimeGridDragReturn {
  dragState: DragState | null;
  isDragging: boolean;
  /** Call on pointerdown on event body */
  handleItemPointerDown: (
    e: React.PointerEvent,
    itemId: string,
    startTime: number,
    endTime: number,
    dayDate: Date
  ) => void;
  /** Call on pointerdown on resize handle */
  handleResizePointerDown: (
    e: React.PointerEvent,
    itemId: string,
    startTime: number,
    endTime: number,
    dayDate: Date,
    edge: 'top' | 'bottom'
  ) => void;
  /** Call on pointerdown on empty slot */
  handleSlotPointerDown: (
    e: React.PointerEvent,
    dayDate: Date
  ) => void;
  /** Ghost position for drag preview: { top, height } in px */
  ghostPosition: { top: number; height: number } | null;
}

const FIFTEEN_MIN_MS = 15 * 60 * 1000;
const MIN_DURATION_DEFAULT = 15 * 60 * 1000;

export function useTimeGridDrag({
  startHour = DEFAULT_START_HOUR,
  snapIntervalMs = FIFTEEN_MIN_MS,
  minDurationMs = MIN_DURATION_DEFAULT,
  onUpdateSchedule,
  onSlotClick,
}: UseTimeGridDragOptions): UseTimeGridDragReturn {
  const [dragState, setDragState] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const isDraggingRef = useRef(false);

  const getDayStartMs = useCallback(
    (dayDate: Date) =>
      startOfDay(dayDate).getTime() + startHour * 60 * 60 * 1000,
    [startHour]
  );

  const yToTime = useCallback(
    (y: number, dayDate: Date) => {
      const dayStartMs = getDayStartMs(dayDate);
      return dayStartMs + y / PIXELS_PER_MS;
    },
    [getDayStartMs]
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const state = dragRef.current;
      if (!state) return;

      // Get grid container for coordinate calculation
      const gridEl = (e.target as HTMLElement).closest?.(
        '[data-time-grid-column]'
      );
      if (!gridEl) return;

      const rect = gridEl.getBoundingClientRect();
      const y = e.clientY - rect.top;

      if (state.mode === 'move') {
        const pointerTime = yToTime(y - state.pointerOffsetY, state.dayDate);
        const snappedStart = snapToSlot(pointerTime, snapIntervalMs);
        const duration = state.originalEndTime - state.originalStartTime;
        const snappedEnd = snappedStart + duration;

        const newState = {
          ...state,
          currentStartTime: snappedStart,
          currentEndTime: snappedEnd,
        };
        dragRef.current = newState;
        setDragState(newState);
      } else if (state.mode === 'resize-bottom') {
        const pointerTime = yToTime(y, state.dayDate);
        const snappedEnd = snapToSlot(pointerTime, snapIntervalMs);
        const minEnd = state.currentStartTime + minDurationMs;

        const newState = {
          ...state,
          currentEndTime: Math.max(snappedEnd, minEnd),
        };
        dragRef.current = newState;
        setDragState(newState);
      } else if (state.mode === 'resize-top') {
        const pointerTime = yToTime(y, state.dayDate);
        const snappedStart = snapToSlot(pointerTime, snapIntervalMs);
        const maxStart = state.currentEndTime - minDurationMs;

        const newState = {
          ...state,
          currentStartTime: Math.min(snappedStart, maxStart),
        };
        dragRef.current = newState;
        setDragState(newState);
      }
    },
    [yToTime, snapIntervalMs, minDurationMs]
  );

  const handlePointerUp = useCallback(() => {
    const state = dragRef.current;
    if (state) {
      // Only commit if position actually changed
      if (
        state.currentStartTime !== state.originalStartTime ||
        state.currentEndTime !== state.originalEndTime
      ) {
        onUpdateSchedule(
          state.itemId,
          state.currentStartTime,
          state.currentEndTime
        );
      }
    }

    dragRef.current = null;
    isDraggingRef.current = false;
    setDragState(null);

    document.removeEventListener('pointermove', handlePointerMove);
    document.removeEventListener('pointerup', handlePointerUp);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, [onUpdateSchedule, handlePointerMove]);

  const startDrag = useCallback(
    (state: DragState) => {
      dragRef.current = state;
      isDraggingRef.current = true;
      setDragState(state);

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor =
        state.mode === 'move'
          ? 'grabbing'
          : 'ns-resize';
    },
    [handlePointerMove, handlePointerUp]
  );

  const handleItemPointerDown = useCallback(
    (
      e: React.PointerEvent,
      itemId: string,
      startTime: number,
      endTime: number,
      dayDate: Date
    ) => {
      e.preventDefault();
      e.stopPropagation();

      const gridEl = (e.target as HTMLElement).closest(
        '[data-time-grid-column]'
      );
      if (!gridEl) return;

      const rect = gridEl.getBoundingClientRect();
      const dayStartMs = getDayStartMs(dayDate);
      const itemTopY = (startTime - dayStartMs) * PIXELS_PER_MS;
      const pointerY = e.clientY - rect.top;

      startDrag({
        itemId,
        mode: 'move',
        originalStartTime: startTime,
        originalEndTime: endTime,
        currentStartTime: startTime,
        currentEndTime: endTime,
        dayDate,
        pointerOffsetY: pointerY - itemTopY,
      });
    },
    [getDayStartMs, startDrag]
  );

  const handleResizePointerDown = useCallback(
    (
      e: React.PointerEvent,
      itemId: string,
      startTime: number,
      endTime: number,
      dayDate: Date,
      edge: 'top' | 'bottom'
    ) => {
      e.preventDefault();
      e.stopPropagation();

      startDrag({
        itemId,
        mode: edge === 'top' ? 'resize-top' : 'resize-bottom',
        originalStartTime: startTime,
        originalEndTime: endTime,
        currentStartTime: startTime,
        currentEndTime: endTime,
        dayDate,
        pointerOffsetY: 0,
      });
    },
    [startDrag]
  );

  const handleSlotPointerDown = useCallback(
    (e: React.PointerEvent, dayDate: Date) => {
      if (isDraggingRef.current) return;

      const gridEl = (e.target as HTMLElement).closest(
        '[data-time-grid-column]'
      );
      if (!gridEl) return;

      const rect = gridEl.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const timeMs = yToTime(y, dayDate);
      const snappedTime = snapToSlot(timeMs, snapIntervalMs);

      onSlotClick?.(dayDate, snappedTime);
    },
    [yToTime, snapIntervalMs, onSlotClick]
  );

  // Compute ghost position for drag preview
  const ghostPosition = dragState
    ? {
        top:
          (dragState.currentStartTime - getDayStartMs(dragState.dayDate)) *
          PIXELS_PER_MS,
        height:
          (dragState.currentEndTime - dragState.currentStartTime) *
          PIXELS_PER_MS,
      }
    : null;

  return {
    dragState,
    isDragging: dragState !== null,
    handleItemPointerDown,
    handleResizePointerDown,
    handleSlotPointerDown,
    ghostPosition,
  };
}
